'use client';

import { useEffect, useMemo, useState } from 'react';
import { getRestrictedOfferingTombstones } from 'services/explore';
import { getOfferingsBatch } from 'services/offerings';
import { useEligibilityBadges } from 'components/HomepageV3/data/eligibilityBadges';
import {
  formatCurrencyFlexible,
  formatCurrencyToShorthand,
  isLocal,
  isProd,
  isStage,
} from 'utils/helpers';
import type { Company, OfferingCardData, OfferingSection } from './demoData';

interface MoneyValue {
  amount?: number | string;
}

interface RestrictedOffering {
  id?: string;
  slug?: string;
  name?: string;
  content?: string;
  regulationType?: string;
  amountRaised?: MoneyValue | number | string;
  fundsCommitted?: MoneyValue | number | string;
  maximumFundingAmount?: MoneyValue | number | string;
  maximum_funding_amount?: MoneyValue | number | string;
  minimumInvestmentAmount?: MoneyValue | number | string;
  lockedOnWaitlist?: boolean;
  locked_on_waitlist?: boolean;
  coverTombstoneUrl?: string;
  logoUrl?: string;
  companyName?: string;
  companyLegalName?: string;
  demoOnly?: boolean;
}

interface RestrictedOfferingItem {
  offering?: RestrictedOffering;
  hasAccess?: boolean;
}

interface RestrictedOfferingResponse {
  list?: RestrictedOfferingItem[];
}

export type RestrictedOfferingDemoMode = 'locked' | 'allowlisted';

export const restrictedOfferingDemoModeFromParam = (
  value?: string | null,
): RestrictedOfferingDemoMode | undefined => {
  const demoEnvironment = isStage || isLocal || process.env.NEXT_PUBLIC_IS_VERCEL === 'true';
  if (isProd || !demoEnvironment) return undefined;
  return value === 'locked' || value === 'allowlisted' ? value : undefined;
};

export interface RestrictedOfferingTombstones {
  section: OfferingSection | null;
  companies: Record<string, Company>;
  isLoading: boolean;
}

export const insertRestrictedOfferingSection = (
  sections: OfferingSection[] | undefined,
  restrictedSection: OfferingSection | null,
): OfferingSection[] | undefined => {
  if (!restrictedSection) return sections;
  const withoutExisting = (sections ?? []).filter(
    (section) => section.title !== restrictedSection.title,
  );
  const preIpoIndex = withoutExisting.findIndex((section) => section.title === 'Pre-IPO Deals');
  const insertionIndex = preIpoIndex < 0 ? 0 : preIpoIndex + 1;
  return [
    ...withoutExisting.slice(0, insertionIndex),
    restrictedSection,
    ...withoutExisting.slice(insertionIndex),
  ];
};

const restrictedOfferingDemoResponse = (
  mode: RestrictedOfferingDemoMode,
): RestrictedOfferingResponse => {
  const visibleDemoOfferings: RestrictedOfferingItem[] = [
    {
      hasAccess: true,
      offering: {
        id: 'restricted-demo-allowlisted-one',
        slug: 'restricted-demo-allowlisted-one',
        name: 'Allowlisted Demo Opportunity',
        content: 'A representative restricted offering visible to this signed-in investor.',
        regulationType: 'REG_D',
        amountRaised: 4800000,
        maximumFundingAmount: 6000000,
        minimumInvestmentAmount: 25000,
        companyName: 'Demo Issuer One, Inc.',
        demoOnly: true,
      },
    },
    {
      hasAccess: true,
      offering: {
        id: 'restricted-demo-allowlisted-two',
        slug: 'restricted-demo-allowlisted-two',
        name: 'Second Allowlisted Demo',
        content: 'A second representative opportunity used to verify allowlist ordering.',
        regulationType: 'REG_D',
        amountRaised: 2100000,
        maximumFundingAmount: 5000000,
        minimumInvestmentAmount: 10000,
        companyName: 'Demo Issuer Two, LLC',
        demoOnly: true,
      },
    },
  ];
  const lockedDemoOfferings: RestrictedOfferingItem[] = Array.from({ length: 4 }, () => ({
    hasAccess: false,
  }));
  return {
    list:
      mode === 'allowlisted'
        ? [...visibleDemoOfferings, ...lockedDemoOfferings.slice(0, 2)]
        : lockedDemoOfferings,
  };
};

const amountOf = (value?: MoneyValue | number | string): number | undefined => {
  const raw = typeof value === 'object' && value !== null ? value.amount : value;
  const parsed =
    typeof raw === 'number' ? raw : Number.parseFloat(String(raw ?? '').replace(/[^0-9.-]/g, ''));
  return Number.isFinite(parsed) ? parsed : undefined;
};

const hydrateAccessibleOfferings = async (
  response: RestrictedOfferingResponse,
): Promise<RestrictedOfferingResponse> => {
  const accessibleSlugs = (response.list ?? [])
    .filter(({ hasAccess, offering }) => hasAccess === true && !offering?.demoOnly)
    .map(({ offering }) => offering?.slug?.trim())
    .filter((slug): slug is string => Boolean(slug));

  if (accessibleSlugs.length === 0) return response;

  const detailsBySlug = await getOfferingsBatch(accessibleSlugs);
  return {
    ...response,
    list: (response.list ?? []).map((item) => {
      const offering = item.offering;
      const slug = offering?.slug?.trim();
      if (item.hasAccess !== true || !offering || !slug) return item;

      const details = detailsBySlug.get(slug);
      if (!details) return item;

      return {
        ...item,
        offering: {
          ...offering,
          amountRaised: details.amountRaised ?? details.fundsCommitted ?? offering.amountRaised,
          fundsCommitted: details.fundsCommitted ?? offering.fundsCommitted,
          maximumFundingAmount:
            details.maximumFundingAmount ??
            details.maximum_funding_amount ??
            offering.maximumFundingAmount ??
            offering.maximum_funding_amount,
          minimumInvestmentAmount:
            details.minimumInvestmentAmount ?? offering.minimumInvestmentAmount,
          lockedOnWaitlist:
            details.lockedOnWaitlist ??
            details.locked_on_waitlist ??
            offering.lockedOnWaitlist ??
            offering.locked_on_waitlist,
        },
      };
    }),
  };
};

export const buildRestrictedOfferingTombstones = (
  response?: RestrictedOfferingResponse,
  eligibilityBadges: Record<string, string> = {},
): Omit<RestrictedOfferingTombstones, 'isLoading'> => {
  const companies: Record<string, Company> = {};
  const cards: OfferingCardData[] = [];

  const items = response?.list ?? [];
  const orderedItems = [
    ...items.filter(({ hasAccess }) => hasAccess === true),
    ...items.filter(({ hasAccess }) => hasAccess !== true),
  ];

  orderedItems.forEach(({ offering, hasAccess }, index) => {
    const accessible = hasAccess === true;
    if (!accessible) {
      const placeholderKey = `restricted-offering-${index + 1}`;
      companies[placeholderKey] = {
        key: placeholderKey,
        name: 'Restricted Offering',
        series: 'Restricted Offering',
        sector: '',
        invested: '',
        desc: '',
        reg: 'Reg D via StartEngine Primary, LLC',
        regD: true,
      };
      cards.push({
        co: placeholderKey,
        locked: true,
        meta: 'Restricted access',
        figR: { lbl: 'Availability', val: 'Restricted', cls: 'urg' },
      });
      return;
    }
    if (!offering) return;
    const slug = offering.slug?.trim();
    const name = offering.name?.trim();
    if (!slug || !name) return;
    const raised = amountOf(offering.amountRaised ?? offering.fundsCommitted);
    const maximum = amountOf(offering.maximumFundingAmount ?? offering.maximum_funding_amount);
    const minimum = amountOf(offering.minimumInvestmentAmount);
    const regulation = offering.regulationType ?? 'REG_D';
    const crowd = regulation === 'REG_CF' || regulation === 'REG_A_PLUS';
    const showRaised = raised !== undefined && raised > 0;
    const hasFundingCeiling = maximum !== undefined && maximum > 0;
    const calculatedFill =
      raised !== undefined && hasFundingCeiling
        ? Math.max(0, Math.min(100, Math.round((raised / maximum) * 100)))
        : undefined;
    const waitlisted =
      offering.lockedOnWaitlist === true ||
      offering.locked_on_waitlist === true ||
      (calculatedFill !== undefined && calculatedFill >= 100);
    let privateFigures: Pick<OfferingCardData, 'figR' | 'fill'>;
    if (waitlisted) {
      privateFigures = {
        figR: { lbl: 'Availability', val: 'Waitlist', cls: 'urg' },
        fill: { w: 100, cls: 'urg' },
      };
    } else if (calculatedFill !== undefined) {
      const fillClass = calculatedFill >= 85 ? 'urg' : 'ok';
      privateFigures = {
        figR: {
          lbl: 'Availability',
          val: `${100 - calculatedFill}% left`,
          cls: fillClass,
        },
        fill: { w: calculatedFill, cls: fillClass },
      };
    } else {
      privateFigures = {
        figR: { lbl: 'Availability', val: 'Open', cls: 'ok' },
      };
    }

    companies[slug] = {
      key: slug,
      name,
      series: name,
      companyName: offering.companyName ?? offering.companyLegalName,
      sector: '',
      invested: showRaised ? formatCurrencyToShorthand(raised) : '',
      desc: offering.content ?? '',
      reg: crowd
        ? `${regulation === 'REG_CF' ? 'Reg CF' : 'Reg A+'} via StartEngine Primary, LLC`
        : 'Reg D via StartEngine Primary, LLC',
      regcf: regulation === 'REG_CF',
      regA: regulation === 'REG_A_PLUS',
      regD: regulation === 'REG_D',
      logo: offering.logoUrl || offering.coverTombstoneUrl,
      fit: offering.logoUrl ? 'contain' : 'cover',
      href: offering.demoOnly ? undefined : `/offering/${slug}`,
    };

    cards.push({
      co: slug,
      locked: false,
      seriesSubhead: companies[slug].reg,
      ...(eligibilityBadges[slug.toLowerCase()]
        ? { meta: eligibilityBadges[slug.toLowerCase()] }
        : {}),
      ...(showRaised ? { figL: { lbl: 'Invested', val: formatCurrencyToShorthand(raised) } } : {}),
      ...(!crowd
        ? privateFigures
        : {
            figR:
              minimum === undefined
                ? {
                    lbl: 'Availability',
                    val: 'Open',
                    cls: 'ok',
                  }
                : {
                    lbl: 'Minimum',
                    val: formatCurrencyFlexible(Math.round(minimum)),
                    cls: 'amber',
                  },
          }),
    });
  });

  if (cards.length === 0) return { section: null, companies };
  return {
    companies,
    section: {
      eyebrow: 'Limited access',
      title: 'Restricted Offerings',
      desc: 'Private opportunities available to eligible investors',
      cards,
      tip: 'Access is limited to investors approved for each offering. Ordering is automated and is not a recommendation.',
    },
  };
};

export default function useRestrictedOfferingTombstones({
  enabled = true,
  userKey,
  demoMode,
}: {
  enabled?: boolean;
  userKey?: string;
  demoMode?: RestrictedOfferingDemoMode;
} = {}): RestrictedOfferingTombstones {
  const eligibilityBadges = useEligibilityBadges();
  const requestKey = userKey ?? 'anonymous';
  const [responseState, setResponseState] = useState<{
    key: string;
    response?: RestrictedOfferingResponse;
    isLoading: boolean;
  }>({ key: requestKey, isLoading: enabled });

  useEffect(() => {
    let active = true;
    if (!enabled) {
      setResponseState({ key: requestKey, isLoading: false });
      return () => {
        active = false;
      };
    }

    if (demoMode) {
      setResponseState({
        key: requestKey,
        response: restrictedOfferingDemoResponse(demoMode),
        isLoading: false,
      });
      return () => {
        active = false;
      };
    }

    setResponseState({ key: requestKey, isLoading: true });
    getRestrictedOfferingTombstones({ page: 0, size: 100 })
      .then(hydrateAccessibleOfferings)
      .then((response) => {
        if (active) setResponseState({ key: requestKey, response, isLoading: false });
      })
      .catch(() => {
        if (active) setResponseState({ key: requestKey, isLoading: false });
      });

    return () => {
      active = false;
    };
  }, [demoMode, enabled, requestKey]);

  const currentResponse = responseState.key === requestKey ? responseState.response : undefined;
  const built = useMemo(
    () => buildRestrictedOfferingTombstones(currentResponse, eligibilityBadges),
    [currentResponse, eligibilityBadges],
  );
  return {
    ...built,
    isLoading: enabled && (responseState.key !== requestKey || responseState.isLoading),
  };
}
